import {
  BufferAttribute,
  BufferGeometry,
  Mesh,
  Object3D,
  type Material,
} from "three";

type GpuAttr = {
  isInterleavedBufferAttribute?: boolean;
  array?: ArrayLike<number>;
  count?: number;
};

/** three-gpu-pathtracer only supports classic CPU-backed mesh geometries. */
export function isPathTraceCompatibleMesh(object: Object3D): object is Mesh {
  const typed = object as Object3D & {
    isMesh?: boolean;
    isLine?: boolean;
    isLineSegments?: boolean;
    isPoints?: boolean;
    isSprite?: boolean;
  };
  if (
    !typed.isMesh ||
    typed.isLine ||
    typed.isLineSegments ||
    typed.isPoints ||
    typed.isSprite
  ) {
    return false;
  }
  const mesh = object as Mesh;
  const geometry = mesh.geometry as BufferGeometry | undefined;
  if (!geometry?.attributes?.position) return false;

  if (!Array.isArray(geometry.groups)) {
    geometry.groups = [];
  }

  const position = geometry.attributes.position as BufferAttribute & GpuAttr;
  if (
    !position.isInterleavedBufferAttribute &&
    (position.array == null || position.count == null)
  ) {
    return false;
  }

  const materials = (
    Array.isArray(mesh.material) ? mesh.material : [mesh.material]
  ).filter(Boolean) as Material[];
  if (materials.length === 0) return false;
  if (
    materials.some(
      (m) =>
        (m as Material & { isNodeMaterial?: boolean }).isNodeMaterial === true,
    )
  ) {
    return false;
  }
  return true;
}

/**
 * Hide objects the pathtracer cannot bake. Caller must restore via the returned
 * restore() (also safe if called twice).
 */
export function hidePathTraceIncompatible(root: Object3D): {
  meshCount: number;
  restore: () => void;
} {
  const hidden: Object3D[] = [];
  let meshCount = 0;
  let restored = false;

  root.traverse((object) => {
    if (!object.visible) return;

    const typed = object as Object3D & {
      isMesh?: boolean;
      isLine?: boolean;
      isLineSegments?: boolean;
      isPoints?: boolean;
      isSprite?: boolean;
      isLight?: boolean;
      isCamera?: boolean;
    };

    if (typed.isLight || typed.isCamera) return;

    if (typed.isMesh) {
      if (isPathTraceCompatibleMesh(object)) {
        meshCount++;
        return;
      }
      object.visible = false;
      hidden.push(object);
      return;
    }

    if (
      typed.isLine ||
      typed.isLineSegments ||
      typed.isPoints ||
      typed.isSprite
    ) {
      object.visible = false;
      hidden.push(object);
    }
  });

  return {
    meshCount,
    restore: () => {
      if (restored) return;
      restored = true;
      for (const object of hidden) object.visible = true;
    },
  };
}

function extractGroupGeometryNonIndexed(
  geometry: BufferGeometry,
  group: { start: number; count: number },
): BufferGeometry {
  const index = geometry.index;
  const out = new BufferGeometry();
  const vertCount = Math.max(0, group.count);

  for (const name of Object.keys(geometry.attributes)) {
    const attr = geometry.attributes[name]!;
    const itemSize = attr.itemSize;
    const array = new Float32Array(vertCount * itemSize);
    for (let i = 0; i < vertCount; i++) {
      const src =
        index != null ? index.getX(group.start + i) : group.start + i;
      for (let k = 0; k < itemSize; k++) {
        array[i * itemSize + k] = attr.getComponent(src, k);
      }
    }
    out.setAttribute(
      name,
      new BufferAttribute(array, itemSize, attr.normalized),
    );
  }

  out.groups = [];
  return out;
}

/**
 * Expand multi-material meshes into temporary single-material meshes for
 * PathTracingSceneGenerator, then restore.
 */
export function expandMultiMaterialMeshesForPathTrace(root: Object3D): {
  restore: () => void;
} {
  type Stash = {
    mesh: Mesh;
    visible: boolean;
    material: Material | Material[];
  };
  const stashed: Stash[] = [];
  const temps: Mesh[] = [];
  let restored = false;

  const multi: Mesh[] = [];
  root.traverse((object) => {
    if (!object.visible || !isPathTraceCompatibleMesh(object)) return;
    if (!Array.isArray(object.material) || object.material.length <= 1) return;
    multi.push(object);
  });

  for (const mesh of multi) {
    const materials = mesh.material as Material[];
    const geometry = mesh.geometry as BufferGeometry;
    const index = geometry.index;
    const total =
      index?.count ?? geometry.attributes.position?.count ?? 0;

    stashed.push({
      mesh,
      visible: mesh.visible,
      material: mesh.material,
    });

    if (!geometry.groups.length || total <= 0) {
      mesh.material = materials[0]!;
      continue;
    }

    mesh.visible = false;
    const parent = mesh.parent;
    if (!parent) continue;

    for (const group of geometry.groups) {
      if (group.count <= 0) continue;
      const materialIndex = group.materialIndex ?? 0;
      const mat = materials[materialIndex] ?? materials[0];
      if (!mat) continue;

      const geo = extractGroupGeometryNonIndexed(geometry, group);
      const temp = new Mesh(geo, mat);
      temp.name = `${mesh.name || "mesh"}__pt${materialIndex}`;
      temp.layers.mask = mesh.layers.mask;
      temp.castShadow = mesh.castShadow;
      temp.receiveShadow = mesh.receiveShadow;
      temp.matrixAutoUpdate = false;
      temp.matrix.copy(mesh.matrix);
      parent.add(temp);
      temp.matrixWorld.copy(mesh.matrixWorld);
      temps.push(temp);
    }
  }

  return {
    restore: () => {
      if (restored) return;
      restored = true;
      for (const temp of temps) {
        temp.removeFromParent();
        temp.geometry.dispose();
      }
      for (const entry of stashed) {
        entry.mesh.material = entry.material;
        entry.mesh.visible = entry.visible;
      }
    },
  };
}
